import { useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
  Alert,
  Modal,
} from "antd";
import {
  DownloadOutlined,
  PlusOutlined,
  CopyOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { devicesApi } from "../api/devices";
import { useDeviceProfiles } from "../hooks/useDeviceProfiles";
import { useDeviceList } from "../hooks/useDevices";

const { Title, Text } = Typography;

export default function DeviceProvision() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { data: deviceList, refetch } = useDeviceList({ page_size: 100 });
  const { data: profiles } = useDeviceProfiles();
  const [resultModal, setResultModal] = useState<{
    devices: Array<{ name: string; device_id: string; api_key: string }>;
  } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [rotate, setRotate] = useState(false);
  const count = Form.useWatch("count", form) || 5;

  // ── 批量创建 ──────────────────────────────────────────────────
  const handleBatchCreate = async (values: {
    name_prefix: string;
    count: number;
    profile_id?: string;
  }) => {
    setLoading(true);
    try {
      const res = await devicesApi.batchCreate(values);
      setResultModal(res);
      form.resetFields();
      refetch();
      message.success(`成功创建 ${res.devices.length} 个设备`);
    } catch (err: unknown) {
      message.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // ── 导出 ──────────────────────────────────────────────────────
  const handleExport = async (includeProvisioned: boolean, forceRotate = false) => {
    setExporting(true);
    try {
      const devices = await devicesApi.exportDevices(includeProvisioned, forceRotate);
      if (devices.length === 0) {
        message.warning("没有可导出的设备");
        return;
      }
      if (forceRotate) {
        message.warning("已强制重新生成 Key，旧固件的 API Key 将立即失效！");
      }

      // 根据 VITE_API_BASE_URL 确定后端地址和端口
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string) || "";
      const isAbsolute = apiBase.startsWith("http");
      // 开发模式（VITE_API_BASE_URL=http://localhost:8000）→ 直接用
      // Docker 模式（VITE_API_BASE_URL 为空）→ 同域走 Nginx 反代
      const backendOrigin = isAbsolute ? apiBase : window.location.origin;
      const httpPort = isAbsolute
        ? Number(new URL(apiBase).port) || 8000
        : Number(window.location.port) ||
          (window.location.protocol === "https:" ? 443 : 80);

      const host = window.location.hostname || "localhost";

      const configs = devices.map((d) => ({
        device_id: d.device_id,
        api_key: d.api_key,
        name: d.name,
        server: {
          host,
          http_port: httpPort,
        },
        mqtt: {
          host,
          port: 1883,
          // 硬件设备通过 MQTT TCP 连接，topic 需携带 owner_id
          topic_command_set: `iot/${d.owner_id || "_"}/${d.device_id}/commands/set`,
          topic_property_report: `iot/${d.owner_id || "_"}/${d.device_id}/properties/report`,
        },
        endpoints: {
          heartbeat: `${backendOrigin}/api/v1/heartbeat/${d.device_id}`,
          properties: `${backendOrigin}/api/v1/devices/${d.device_id}/properties`,
          text_interact: `${backendOrigin}/api/v1/interact/text`,
          voice_interact: `${backendOrigin}/api/v1/interact/voice`,
        },
      }));

      const blob = new Blob([JSON.stringify(configs, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `firmware-config-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      message.success(`已导出 ${configs.length} 个设备配置`);
    } catch (err: unknown) {
      message.error((err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const unprovisionedCount =
    deviceList?.items.filter((d) => !d.last_seen).length || 0;

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>
        烧录管理
      </Title>

      <Row gutter={16}>
        {/* 批量创建 */}
        <Col xs={24} lg={12}>
          <Card title="批量创建设备" size="small">
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginBottom: 16 }}
            >
              批量生成设备凭证，用于烧录到固件中。创建后请立即导出配置。
            </Text>
            <Form form={form} layout="vertical" onFinish={handleBatchCreate}>
              <Form.Item
                name="name_prefix"
                label="设备名称前缀"
                rules={[{ required: true, message: "请输入前缀" }]}
              >
                <Input placeholder="如：智能插座、温湿度计" />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    name="count"
                    label="数量"
                    rules={[{ required: true, message: "请输入数量" }]}
                    initialValue={1}
                  >
                    <InputNumber min={1} max={100} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="profile_id"
                    label="设备型号"
                    rules={[{ required: true, message: "请选择设备型号" }]}
                  >
                    <Select
                      placeholder="选择设备型号"
                      options={profiles?.map((p) => ({
                        value: p.id,
                        label: `${p.display_name} (${p.category === "sensor" ? "传感器" : "执行器"})`,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
                loading={loading}
                block
              >
                创建 {count} 个设备
              </Button>
            </Form>
          </Card>
        </Col>

        {/* 导出区 */}
        <Col xs={24} lg={12}>
          <Card title="导出烧录配置" size="small">
            <div style={{ marginBottom: 16 }}>
              <Alert
                type="info"
                message={
                  <span>
                    当前有 <Text strong>{unprovisionedCount}</Text>{" "}
                    个设备未上线（未烧录）
                  </span>
                }
                showIcon
              />
            </div>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleExport(false, rotate)}
                loading={exporting}
                block
                type="primary"
              >
                导出未烧录设备（{unprovisionedCount} 个）
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleExport(true, rotate)}
                loading={exporting}
                block
              >
                导出全部设备（含已上线）
              </Button>
              <Checkbox
                checked={rotate}
                onChange={(e) => setRotate(e.target.checked)}
                style={{ fontSize: 12 }}
              >
                强制重新生成 Key（旧固件 API Key 将立即失效）
              </Checkbox>
              {rotate && (
                <Alert
                  type="warning"
                  message="勾选后导出，所有已烧录设备的旧 Key 将作废，需重新烧录固件。"
                  showIcon
                  style={{ fontSize: 12 }}
                />
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* 设备列表 */}
      <Card
        title="我的设备"
        size="small"
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            共 {deviceList?.total || 0} 台 ·{" "}
            {deviceList?.items.filter((d) => d.last_seen).length || 0} 台已上线
          </Text>
        }
      >
        <Table
          dataSource={deviceList?.items}
          rowKey="id"
          size="small"
          pagination={false}
          columns={[
            { title: "名称", dataIndex: "name", width: 160 },
            {
              title: "烧录状态",
              key: "provisioned",
              width: 100,
              render: (_: unknown, r: { last_seen?: string }) =>
                r.last_seen ? (
                  <Tag icon={<CheckCircleOutlined />} color="green">
                    已上线
                  </Tag>
                ) : (
                  <Tag>待烧录</Tag>
                ),
            },
            {
              title: "型号",
              dataIndex: "profile_id",
              width: 120,
              render: (pid: string | null) =>
                pid ? (
                  <Tag color="blue">
                    {profiles?.find((p) => p.id === pid)?.display_name || pid}
                  </Tag>
                ) : (
                  "—"
                ),
            },
            {
              title: "状态",
              dataIndex: "status",
              width: 80,
              render: (s: string) => (
                <Tag color={s === "online" ? "green" : "default"}>
                  {s === "online" ? "在线" : "离线"}
                </Tag>
              ),
            },
            {
              title: "创建时间",
              dataIndex: "created_at",
              width: 120,
              render: (t: string) => new Date(t).toLocaleDateString("zh-CN"),
            },
          ]}
          locale={{ emptyText: "暂无设备" }}
        />
      </Card>

      {/* 创建结果弹窗 */}
      <Modal
        title="批量创建成功"
        open={!!resultModal}
        onCancel={() => setResultModal(null)}
        footer={
          <Button type="primary" onClick={() => setResultModal(null)}>
            完成
          </Button>
        }
        width={640}
      >
        {resultModal && (
          <div>
            <Alert
              type="success"
              message={`成功创建 ${resultModal.devices.length} 个设备`}
              description="请立即导出烧录配置，API Key 只在此处展示。"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Table
              dataSource={resultModal.devices}
              rowKey="device_id"
              size="small"
              pagination={false}
              columns={[
                { title: "名称", dataIndex: "name", width: 120 },
                {
                  title: "Device ID",
                  dataIndex: "device_id",
                  width: 200,
                  render: (v: string) => (
                    <Space>
                      <code style={{ fontSize: 11 }}>{v.slice(0, 12)}...</code>
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          navigator.clipboard.writeText(v);
                          message.success("已复制");
                        }}
                      />
                    </Space>
                  ),
                },
                {
                  title: "API Key",
                  dataIndex: "api_key",
                  width: 260,
                  render: (v: string) => (
                    <Space>
                      <code style={{ fontSize: 11 }}>{v.slice(0, 20)}...</code>
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          navigator.clipboard.writeText(v);
                          message.success("已复制");
                        }}
                      />
                    </Space>
                  ),
                },
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
